import React from 'react';
import { importWithChunkRecovery } from 'shared/services/appUpdate';

/**
 * Lazy route loading with stale-build recovery.
 *
 * Every route chunk goes through `importWithChunkRecovery`, which retries once
 * and then reloads the tab when a deploy has replaced the chunk this build was
 * built against — see that function for why the global handlers in
 * `AppUpdateProvider` can't cover this path.
 */
export function lazyNamed<T extends React.ComponentType<any>>(
  loader: () => Promise<Record<string, unknown>>,
  exportName: string,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    const module = await importWithChunkRecovery(loader);
    return { default: module[exportName] as T };
  });
}

/** As `lazyNamed`, for a module whose component is the default export. */
export function lazyDefault<T extends React.ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() => importWithChunkRecovery(loader));
}
